import React from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { formValueSelector, change, clearSubmitErrors } from "redux-form";
import { formName } from "./AddClaimPage";
import { UNLIMITED_AMOUNT, FormattedCustomDate } from "../common/FormattedComponents";
import messages from "../messagesDescription";
import {
  getClaimItem,
  getBenefitPlan,
  getFieldsByPlanIdAndItemType,
  augmentClaimFieldsProps,
} from "../../reducers/claimsReducer";
import {
  loadClaimItemAccounts,
  loadBenefitPlans,
  loadClaimItems,
  loadClaimItemFields,
  loadShouldDisplayClaimItemsFields,
  loadClaimLimitMessages,
  changeReferralFileName,
  changeCurrencyCode,
  changeCurrencyRate,
} from "../../actions/claimActions";
import { showToast } from "../../actions/toastActions";
import {
  isExist,
  compose,
  getPropsBySelector,
  getDateRangeFromBenefitPlan,
  getToday,
} from "cxa-utils";
import StandardMoney from "../common/StandardMoney";

const {
  accountDescription,
  accountOwnerDescription,
  periodDescription,
  entitlementDescription,
  utilisedDescription,
  balanceDescription,
  pendingAmountDescription,
} = messages;

const _getPropsBySelector = compose(getPropsBySelector, formValueSelector)(formName);

function getClaimItemAccountsHeaders(intl) {
  return (fields = []) => {
    const localeMessages = [
      accountDescription,
      accountOwnerDescription,
      periodDescription,
      entitlementDescription,
      utilisedDescription,
      balanceDescription,
      pendingAmountDescription,
    ];
    const replacedFields = fields.map(field => field.split(/(?=[A-Z])/).join(" "));
    const re = new RegExp(`^${replacedFields.join("|")}$`);

    return localeMessages.reduce((acc, locale) => {
      const { defaultMessage } = locale; // as key

      if (re.test(defaultMessage)) {
        return [...acc, intl.formatMessage(locale)];
      }

      return [...acc, ""];
    }, []);
  };
}

function getClaimItemAccountsRows(intl) {
  return ({ accounts = [], headers }) =>
    accounts.map(item => {
      const {
        displayName,
        owner,
        period,
        entitlement,
        balance,
        utilized,
        pendingReceiptAmount,
        accountCurrency,
      } = item;
      const { text, from, to } = period;
      const accountRow = intl.formatMessage({
        id: displayName,
        defaultMessage: displayName,
      });
      const { name: accountOwnerRow } = owner;
      const periodRow = text || (
        <span key={`${text}-${from}-${to}`}>
          <FormattedCustomDate value={from} /> - <FormattedCustomDate value={to} />
        </span>
      );
      const entitlementRow = (
        <StandardMoney key={entitlement} value={entitlement} currency={accountCurrency} />
      );
      const utilisedRow = (
        <StandardMoney key={utilized} value={utilized} currency={accountCurrency} />
      );
      const balanceRow = (
        <StandardMoney
          key={balance}
          value={balance}
          currency={accountCurrency}
          isUnlimited={entitlement === UNLIMITED_AMOUNT}
        />
      );
      const pendingAmountRow = (
        <StandardMoney
          key={pendingReceiptAmount}
          value={pendingReceiptAmount}
          currency={accountCurrency}
        />
      );

      return [
        accountRow,
        accountOwnerRow,
        periodRow,
        entitlementRow,
        utilisedRow,
        balanceRow,
        pendingAmountRow,
      ].filter((row, idx) => isExist(headers[idx]));
    });
}

function mapStateToProps(state, ownProps) {
  const { login, claims, httpIntercept } = state;
  const { intl, goBack, match } = ownProps;
  const {
    claimItems,
    benefitPlans,
    claimItemAccounts,
    claimLimitMessages,
    toCurrencyCode,
    configuredClaimItemFields,
    fields = {},
  } = claims;
  const { error } = httpIntercept;
  const { params } = match;
  const { benefitPeriod, claimTypeId, selectedDelegator: delegator } = params;

  // section - get date range
  const benefitPlan = getBenefitPlan(benefitPlans, benefitPeriod) || {};
  const { startDate: minDate, endDate } = getDateRangeFromBenefitPlan(benefitPlan);
  const today = getToday();
  const maxDate = today.isAfter(endDate) ? endDate : today;

  // section - get field
  const fieldsById = getFieldsByPlanIdAndItemType(fields, benefitPeriod, claimTypeId) || {};
  const byIdFields = fieldsById.fields || [];
  const allFields = isExist(fieldsById) && {
    ...fieldsById,
    fields: augmentClaimFieldsProps(byIdFields),
  };

  // section - get accounts
  const accountDetails = claimItemAccounts.byId[claimTypeId] || {
    all: [],
    accountsExhausted: false,
  };
  const { accountsExhausted } = accountDetails;
  const accounts = accountDetails.all.map(item => accountDetails.byId[item]);

  // section - get headers, rows
  const headers = getClaimItemAccountsHeaders(intl)(configuredClaimItemFields.fields);
  const rows = getClaimItemAccountsRows(intl)({ accounts, headers });

  // section - get claimant data
  const [claimantNameField = {}] = byIdFields.filter(({ name }) => name === "claimantName");
  const { items = [], defaultAsBlank } = claimantNameField;
  const [defaultClaimant = { value: "" }] = items;
  const { value: defaultClaimantName } = defaultClaimant;

  // section - get data by selector
  const {
    foreignReceiptAmount,
    receiptAmount,
    claimantName = defaultAsBlank || defaultClaimantName,
    receiptDate = maxDate.format("YYYY-MM-DD[T]HH:mm:ss"),
    receiptCurrency = isExist(accounts) && accounts[0].accountCurrency,
  } = _getPropsBySelector(state)(
    "foreignReceiptAmount",
    "receiptDate",
    "receiptCurrency",
    "claimantName"
  );

  // section - get message id
  const [claimantType, claimantId] = claimantName.split("|");
  const messageId = `${claimantId}${claimantType}${claimTypeId}${receiptDate}`;

  // section - get delegator
  const selectedDelegator = delegator ? parseInt(delegator, 10) : null;

  // section - is fetching?
  const isFetching =
    fields.isFetching ||
    benefitPlans.isFetching ||
    claimItems.isFetching ||
    claimItemAccounts.isFetching ||
    !(claimItems && benefitPlan && allFields);

  return {
    benefitPeriod,
    benefitPlan,
    claimTypeId,
    minDate,
    maxDate,
    accountsExhausted,
    login,
    toCurrencyCode,
    foreignReceiptAmount,
    receiptAmount,
    receiptDate,
    claimantType,
    claimantId,
    error,
    selectedDelegator,
    receiptCurrency,
    isFetching,
    rows,
    headers: headers.filter(header => isExist(header)),
    mode: "add",
    limitMessages: claimLimitMessages.byId[messageId],
    limitMessagesFetching: claimLimitMessages.isFetching,
    isCPFEnabled: claimItems.isCPFEnabled || false,
    claimItem: getClaimItem(claimItems, claimTypeId),
    claimInstruction: fields.claimInstruction,
    consentMessageTitle: fields.consentMessageTitle,
    consentMessage: fields.consentMessage,
    fields: allFields,
    userAccepted: false,
    httpError: error,
    initialValues: {
      benefitPeriod,
      claimTypeId,
      claimantName,
      receiptAmount,
      foreignReceiptAmount,
      selectedDelegator,
      receiptCurrency,
      userAccepted: false,
    },
    accountsFetching: claimItemAccounts.isFetching,
    onCancel: goBack,
  };
}

const mapDispatchToProps = {
  loadBenefitPlans,
  loadClaimItems,
  loadClaimItemFields,
  loadClaimItemAccounts,
  loadClaimLimitMessages,
  showToast,
  changeReferralFileName,
  changeCurrencyCode,
  changeCurrencyRate,
  loadShouldDisplayClaimItemsFields,
  clearSubmitErrors,
  changeFieldValue: (field, value) => dispatch => dispatch(change(formName, field, value)),
};

const withClaimContainer = Component =>
  compose(injectIntl, connect(mapStateToProps, mapDispatchToProps))(Component);

export default withClaimContainer;



// WEBPACK FOOTER //
// ./components/claim/withClaimContainer.js